import { Link } from 'react-router-dom';
import { assetUrl } from '#/utils/assetUrl';
import {
  benefitDetailUrl,
  tryGetBenefitServiceSeqNo,
} from '#/domains/landing/utils/benefitDetailUrl';
import { ctaLinkProps } from '#/domains/landing/utils/ctaLink';

interface BenefitCardProps {
  imageUrl?: string;
  title: string;
  description: string;
  supportType: string;
  organization: string;
  href?: string;
  onClick?: () => void;
  contentBase?: string;
  analyticsId?: string;
}

export default function BenefitCard({
  imageUrl,
  title,
  description,
  supportType,
  organization,
  href,
  onClick,
  contentBase,
  analyticsId,
}: BenefitCardProps) {
  const cardContent = (
    <div className="flex w-full items-start gap-4">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[15px] bg-white">
        {imageUrl && (
          <img
            src={assetUrl(imageUrl, contentBase)}
            alt=""
            width={32}
            height={32}
            className="h-8 w-8 object-contain"
          />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex w-full flex-col gap-1">
          <p className="text-lg font-bold leading-[1.5] text-gray-100">
            {title}
          </p>
          <p className="text-sm font-medium leading-[1.5] text-gray-60">
            {description}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-2xl bg-blue-10 px-2.5 py-[3px] text-xs font-bold leading-[1.5] text-blue-50">
            {supportType}
          </span>
          <span className="rounded-2xl bg-blue-10 px-2.5 py-[3px] text-xs font-bold leading-[1.5] text-blue-50">
            {organization}
          </span>
        </div>
      </div>
    </div>
  );

  if (href) {
    const benefitServiceSeqNo = tryGetBenefitServiceSeqNo(href);
    // Benefit detail links open in a new browsing context so the article
    // WebView is preserved underneath in-app — system back returns here.
    if (benefitServiceSeqNo) {
      return (
        <a
          href={ctaLinkProps(benefitDetailUrl(benefitServiceSeqNo)).href}
          id={analyticsId}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full rounded-3xl bg-gray-5 pl-[18px] pr-4 py-[18px] transition-colors active:bg-gray-10"
        >
          {cardContent}
        </a>
      );
    }
    return (
      <Link
        to={href}
        id={analyticsId}
        className="block w-full rounded-3xl bg-gray-5 pl-[18px] pr-4 py-[18px] transition-colors active:bg-gray-10"
      >
        {cardContent}
      </Link>
    );
  }

  if (onClick) {
    return (
      <div
        id={analyticsId}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') onClick();
        }}
        className="w-full cursor-pointer rounded-3xl bg-gray-5 pl-[18px] pr-4 py-[18px] transition-colors active:bg-gray-10"
      >
        {cardContent}
      </div>
    );
  }

  return (
    <div
      id={analyticsId}
      className="w-full rounded-3xl bg-gray-5 pl-[18px] pr-4 py-[18px]"
    >
      {cardContent}
    </div>
  );
}
